import React, { useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en-gb';
import { buildFixedTermComment } from '../../../constants/fixedTermContracts';
import UpcomingEmployeeEvents from '../components/UpcomingEmployeeEvents';
import { contractActionFacts } from '../../../utils/contractActionFacts';
import { toPickerValue } from '../../../utils/dateOnly';

dayjs.extend(utc);

/**
 * ADR-136 phase 5 — "Change End Date" (B1) modal content. One action instead of
 * the historic Withdraw → Resign two-step; the batch preserves the full audit
 * trail (§5a). Valid for any date after the contract start — a future end stays
 * a Pending TERMINATION_FINALIZED, the normal FTC encoding.
 */
const EmployeeChangeContractEnd = ({ employee, contract = null, episodeId = null, onValidChange }) => {
  const [formData, setFormData] = useState({
    newEndDate: null,
    reason: '',
    resignationReason: ''
  });
  const [touched, setTouched] = useState({ newEndDate: false, reason: false });
  // A2 touched-pattern: the generated sentence is regenerated until the operator
  // edits it; then it is theirs.
  const [commentTouched, setCommentTouched] = useState(false);

  // ADR-187 — the acting contract's own facts (passed by ContractActionModals);
  // the person's flat fields are the PRIMARY's and are read only through the
  // resolver, when this is the primary or the payload has no contracts[].
  const facts = contract || contractActionFacts(employee, episodeId ? { id: episodeId } : null);
  const contractStartDate = facts.startDate;
  // ADR-146 §4.1 — keep the "— contract <N>" suffix in the regenerated
  // sentence (previously dropped: null was passed), so the endReasonText the
  // amendment carries matches the sentence the hire surface wrote.
  const contractNumber = facts.contractNumber;
  const currentEndDate = facts.endDate;
  const pendingRenewalOn = facts.pendingRenewalOn;

  const isAfterStart = (dateStr) => (
    !contractStartDate || !dateStr
      ? true
      : dayjs.utc(dateStr).isAfter(dayjs.utc(contractStartDate), 'day')
  );
  const isSameAsCurrent = (dateStr) => (
    Boolean(currentEndDate && dateStr
      && dayjs.utc(dateStr).isSame(dayjs.utc(currentEndDate), 'day'))
  );

  const computeValidity = (data) => Boolean(
    data.newEndDate
    && data.reason
    && String(data.resignationReason || '').trim()
    && isAfterStart(data.newEndDate)
    && !isSameAsCurrent(data.newEndDate)
  );

  const handleDateChange = (newValue) => {
    const newDate = newValue && typeof newValue.isValid === 'function' && newValue.isValid()
      ? newValue.format('YYYY-MM-DD')
      : null;

    const generated = !commentTouched && newDate
      ? (buildFixedTermComment(contractStartDate, newDate, contractNumber) || '')
      : formData.resignationReason;

    const newFormData = {
      ...formData,
      newEndDate: newDate,
      resignationReason: commentTouched ? formData.resignationReason : generated
    };
    setFormData(newFormData);
    setTouched(prev => ({ ...prev, newEndDate: true }));
    onValidChange(computeValidity(newFormData), newFormData);
  };

  const handleInputChange = (field) => (event) => {
    if (field === 'resignationReason') {
      setCommentTouched(true);
    }
    const newFormData = { ...formData, [field]: event.target.value };
    setFormData(newFormData);
    setTouched(prev => ({ ...prev, [field]: true }));
    onValidChange(computeValidity(newFormData), newFormData);
  };

  React.useEffect(() => {
    // Initialize as invalid
    onValidChange(false, formData);
  }, []);

  const dateError = formData.newEndDate
    && (!isAfterStart(formData.newEndDate) || isSameAsCurrent(formData.newEndDate));

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <Alert severity="info">
          This reschedules the contract&apos;s end — <strong>employment continues</strong> until
          the new date. The previous scheduled end is withdrawn (it stays in the audit trail)
          and a new one is recorded in its place; a future date is enacted by the scheduler
          when it arrives. To end the contract early instead, use <em>Cancel Contract</em>;
          to make it open-ended, use <em>Resign Withdraw</em>.
        </Alert>
        {/* ADR-136 §15 — fresh-fetched standing schedule (includes the end
            being moved, and anything else pending). Scoped to the acting
            contract when known (ADR-113 §8). */}
        <UpcomingEmployeeEvents employeeId={employee?.id} episodeId={episodeId} />
        {/* ADR-136 phase 6 (§15 slice 3 refinement): a pending scheduled
            replacement supersedes ends near or after its cutover — warn. */}
        {pendingRenewalOn && (
          <Alert severity="warning">
            A replacement is <strong>scheduled
            for {dayjs.utc(pendingRenewalOn).format('DD/MM/YYYY')}</strong>.
            An end on/after that date — or in the grace window just before it — is withdrawn
            and superseded when the replacement fires; only an end enacted earlier takes real
            effect (the person leaves, and the replacement then still starts the new contract
            as scheduled). To change the replacement plan itself, use <em>Cancel Scheduled
            Renewal</em> first.
          </Alert>
        )}
        {currentEndDate && (
          <Typography variant="body2" color="text.secondary">
            Current end date: <strong>{dayjs.utc(currentEndDate).format('DD/MM/YYYY')}</strong>
            {contractStartDate && (
              <> — contract started {dayjs.utc(contractStartDate).format('DD/MM/YYYY')}</>
            )}
          </Typography>
        )}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DatePicker
            label="New End Date"
            value={toPickerValue(formData.newEndDate)}
            onChange={handleDateChange}
            minDate={contractStartDate ? dayjs.utc(contractStartDate).add(1, 'day') : undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: Boolean((touched.newEndDate && !formData.newEndDate) || dateError),
                helperText: dateError
                  ? (isSameAsCurrent(formData.newEndDate)
                    ? 'The new end date is the same as the current one'
                    : 'The new end date must be after the contract start date')
                  : 'The date the contract will now end — past or future',
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </LocalizationProvider>

        <TextField
          fullWidth
          label="Reason for the change"
          value={formData.reason}
          onChange={handleInputChange('reason')}
          multiline
          rows={2}
          required
          error={touched.reason && !formData.reason}
          helperText={touched.reason && !formData.reason
            ? 'Reason is required'
            : 'Why the end date is changing — stamped on the withdrawal of the previous end'}
          placeholder="Enter reason for changing the end date..."
        />

        {formData.newEndDate && (
          <TextField
            fullWidth
            label="New fixed-term comment"
            value={formData.resignationReason}
            onChange={handleInputChange('resignationReason')}
            multiline
            required
            error={!String(formData.resignationReason || '').trim()}
            helperText={!String(formData.resignationReason || '').trim()
              ? 'Required — becomes the new scheduled end\'s reason'
              : 'Auto-generated from the new term; edit freely.'}
          />
        )}
      </Box>
    </Box>
  );
};

export default EmployeeChangeContractEnd;
